import {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import React from 'react';
import { GcalClient } from './client';
import { GcalContext } from './context';
import {
  GcalSession,
  GcalStatus,
  GcalUser,
  ISignInInfo,
  // UserAttributes,
} from './gcal';

export type GcalProviderProps = React.PropsWithChildren<{
  client: GcalClient;
}>;

enum GCAL_QKEYS {
  GCAL_TOKEN = 'gcal_token',
  GCAL_USER = 'gcal_user',
}

function useGcalQueryParams() {
  return React.useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    const geerd_token = search.get(GCAL_QKEYS.GCAL_TOKEN);
    const geerd_user = search.get(GCAL_QKEYS.GCAL_USER);
    // remove gcal keys
    return {
      geerd_token,
      geerd_user,
    };
  }, []);
}

function removeURLQueryKeys(...params: string[]) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  params.forEach((param) => urlSearchParams.delete(param));
  let search = urlSearchParams.toString();
  search = search ? '?' + search : '';
  window.history.replaceState(
    {},
    '',
    window.location.pathname + search + window.location.hash
  );
}

export function GcalProvider({ client, children }: GcalProviderProps) {
  const [user, setUser] = React.useState<GcalUser | null>(
    client.Pool.getCurrentUser()
  );

  const [groups, setGroups] = React.useState<string[]>([]);
  const [status, setStatus] = React.useState<GcalStatus>(GcalStatus.STARTUP);
  const [error, setError] = React.useState<Error>();
  const { geerd_token, geerd_user } = useGcalQueryParams();

  // -- Callbacks
  const signIn = React.useCallback(
    async (info: ISignInInfo) => {
      const { refreshToken, username } = info;
      const User = new CognitoUser({ Pool: client.Pool, Username: username });
      const RefreshToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });
      // refresh session
      return new Promise((resolve, reject) =>
        User.refreshSession(RefreshToken, (err, res) =>
          err ? reject(err) : resolve(res)
        )
      )
        .then(() => setUser(User))
        .catch((err) => setError(err));
    },
    [client]
  );

  const signOut = React.useCallback(
    (redirect?: string) => {
      user?.signOut(() => {
        localStorage.clear();
        if (redirect) {
          window.location.pathname = redirect;
        } else setUser(null);
      });
    },
    [user]
  );

  const getSession = React.useCallback(
    async () =>
      new Promise<GcalSession>((resolve, reject) => {
        if (!user) return;
        user.getSession((err: Error, session: CognitoUserSession | null) =>
          session && !err ? resolve(session) : reject(err)
        );
      }),
    [user]
  );

  // consume Gcal query params
  React.useEffect(() => {
    if (user) {
      setStatus(GcalStatus.AUTHENTICATED);
      return;
    }
    if (!geerd_token || !geerd_user) {
      setStatus(GcalStatus.UNAUTHENTICATED);
      return;
    }
    signIn({ refreshToken: geerd_token, username: geerd_user }).then(() => {
      removeURLQueryKeys(GCAL_QKEYS.GCAL_TOKEN, GCAL_QKEYS.GCAL_USER);
    });
  }, [signIn, user, geerd_user, geerd_token]);

  // load user attributes
  React.useEffect(() => {
    user?.getSession((err: Error, _: CognitoUserSession | null) => {
      if (err) setError(err);
      else setGroups(_?.getIdToken().decodePayload()['cognito:groups']);
    });
  }, [user]);

  return (
    <GcalContext.Provider
      value={{
        client,
        user,
        // attributes,
        groups,
        status,
        error,
        signIn,
        signOut,
        getSession,
      }}
    >
      {children}
    </GcalContext.Provider>
  );
}
